import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Card,
  CardContent,
  Box,
  Grid,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Alert,
  Divider
} from '@mui/material';
import type { GridProps } from '@mui/material/Grid';
import UserReports from '../components/UserReports';
import UserManagement from '../components/UserManagement';
import PayoutRulesManagement from '../components/PayoutRulesManagement';
import DrawManagement from './DrawManagement';
import OperatorManagement from '../components/OperatorManagement';
import CommissionManagement from '../components/CommissionManagement';
import ResultsEntry from '../components/ResultsEntry';
import SettlementDashboard from './SettlementDashboard';

import {
  Dashboard as DashboardIcon,
  People,
  AccountBalance,
  Settings,
  Download,
  Add,
  Edit,
  Delete,
  Visibility,
  TrendingUp,
  TrendingDown,
  AttachMoney,
  Receipt,
  Search,
  FilterList,
  AccountBalanceWallet,
  MonetizationOn,
  CalendarToday,
  BusinessCenter,
  Percent,
  EmojiEvents,
  Assessment
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface AdminStats {
  totalUsers: number;
  totalBets: number;
  totalWinnings: number;
  totalDeposits: number;
  totalWithdrawals: number;
  pendingWithdrawals: number;
  dailyRevenue: number;
  monthlyRevenue: number;
}

interface User {
  id: number;
  email: string;
  fullName: string;
  credits: number;
  totalBets: number;
  totalWinnings: number;
  lastLoginAt: string;
  isActive: boolean;
  isSuspended: boolean;
}

interface WithdrawalRequest {
  id: number;
  userId: number;
  userEmail: string;
  amount: number;
  bankName: string;
  accountNumber: string;
  accountHolderName: string;
  status: 'pending' | 'approved' | 'rejected' | 'completed';
  requestDate: string;
  processedDate?: string;
}

interface WithdrawalHistoryResponseDto {
  withdrawalRequests: WithdrawalRequest[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  summary: {
    totalAmount: number;
    pendingAmount: number;
    approvedAmount: number;
    rejectedAmount: number;
  };
}

interface BetLimit {
  id: number;
  counter: string;
  number: string;
  maxAmount: number;
  currentAmount: number;
}

interface CreditTransaction {
  id: number;
  userId: number;
  type: 'deposit' | 'withdraw';
  amount: number;
  previousBalance: number;
  newBalance: number;
  adminId: number;
  adminName: string;
  reason?: string;
  timestamp: string;
}

interface AuditLog {
  id: number;
  adminId: number;
  adminName: string;
  action: 'credit_deposit' | 'credit_withdraw' | 'user_status_change' | 'withdrawal_approval' | 'withdrawal_rejection' | 'user_creation' | 'user_deletion';
  targetUserId?: number;
  targetUserEmail?: string;
  details: string;
  previousValue?: string;
  newValue?: string;
  timestamp: string;
  ipAddress?: string;
}

interface Wallet {
  id: number;
  userId: number;
  userEmail: string;
  userName: string;
  balance: number;
  currency: string;
  status: string;
  createdAt: string;
}

interface WalletTransaction {
  id: number;
  walletId: number;
  txType: string;
  amount: number;
  BeforeBalance: number;
  AfterBalance: number;
  createdByUserId: number;
  createdByUserEmail: string;
  note?: string;
  createdAt: string;
}

interface TopupRequest {
  walletId: number;
  amount: number;
  note?: string;
}

interface WalletListResponse {
  wallets: Wallet[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}



function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`backoffice-tabpanel-${index}`}
      aria-labelledby={`backoffice-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const BackOffice: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [userDialog, setUserDialog] = useState(false);
  const [creditDialog, setCreditDialog] = useState(false);
  const [limitDialog, setLimitDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditType, setCreditType] = useState<'deposit' | 'withdraw'>('deposit');
  const [creditReason, setCreditReason] = useState('');
  const [reportDate, setReportDate] = useState<Date | null>(new Date());
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState<string>('all');
  const [showTransactionHistory, setShowTransactionHistory] = useState(false);
  const [showAuditLogs, setShowAuditLogs] = useState(false);
  const [auditLogFilter, setAuditLogFilter] = useState<string>('all');
  const [userSelectionDialog, setUserSelectionDialog] = useState(false);
  const [quickTopUpSearchTerm, setQuickTopUpSearchTerm] = useState('');
  
  // Wallet management state
  const [walletSearchEmail, setWalletSearchEmail] = useState('');
  const [searchedWallet, setSearchedWallet] = useState<Wallet | null>(null);
  const [walletTransactions, setWalletTransactions] = useState<WalletTransaction[]>([]);
  const [topupDialog, setTopupDialog] = useState(false);
  const [manualAdjustDialog, setManualAdjustDialog] = useState(false);
  const [topupAmount, setTopupAmount] = useState('');
  const [topupNote, setTopupNote] = useState('');
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustNote, setAdjustNote] = useState('');
  const [walletListPage, setWalletListPage] = useState(1);
  const [walletListPageSize] = useState(20);
  const [selectedWalletFromList, setSelectedWalletFromList] = useState<Wallet | null>(null);
  
  const queryClient = useQueryClient();

  // Fetch admin statistics
  const { data: adminStats, isLoading: statsLoading, error: statsError } = useQuery<AdminStats>({
    queryKey: ['admin-stats'],
    queryFn: async () => {
      const response = await axios.get('/admin/stats');
      return response.data;
    },
    retry: 1,
    refetchOnWindowFocus: false
  });

  // Fetch users
  const { data: usersResponse, isLoading: usersLoading, error: usersError } = useQuery({
    queryKey: ['admin-users', userSearchTerm, userStatusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (userSearchTerm) params.append('searchTerm', userSearchTerm);
      if (userStatusFilter === 'active') params.append('isActive', 'true');
      if (userStatusFilter === 'suspended') params.append('isSuspended', 'true');
      params.append('page', '1');
      params.append('pageSize', '100');
      
      const response = await axios.get(`/admin/users?${params.toString()}`);
      return response.data;
    },
    retry: 1,
    refetchOnWindowFocus: false
  });
  
  const users = usersResponse?.users || [];

  // Fetch withdrawal requests
  const { data: withdrawalRequests, isLoading: withdrawalsLoading, error: withdrawalsError } = useQuery<WithdrawalRequest[]>({
    queryKey: ['admin-withdrawals'],
    queryFn: async () => {
      const response = await axios.get<WithdrawalHistoryResponseDto>('/admin/withdrawals');
      return response.data.withdrawalRequests || [];
    },
    retry: 1,
    refetchOnWindowFocus: false
  });

  const withdrawals = withdrawalRequests || [];

  // Fetch bet limits
  const { data: betLimits, isLoading: betLimitsLoading, error: betLimitsError } = useQuery<BetLimit[]>({
    queryKey: ['admin-bet-limits'],
    queryFn: async () => {
      const response = await axios.get('/admin/bet-limits');
      return response.data;
    },
    retry: 1,
    refetchOnWindowFocus: false
  });

  // Fetch credit transactions for selected user
  const { data: creditTransactions, isLoading: transactionsLoading, error: transactionsError } = useQuery<CreditTransaction[]>({
    queryKey: ['admin-credit-transactions', selectedUser?.id],
    queryFn: async () => {
      if (!selectedUser?.id) return [];
      const response = await axios.get(`/admin/users/${selectedUser.id}/credit-transactions`);
      return response.data;
    },
    enabled: !!selectedUser?.id && showTransactionHistory,
    retry: 1,
    refetchOnWindowFocus: false
  });

  // Fetch audit logs
  // Helper function to format date as YYYY-MM-DD in local timezone
  const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const { data: auditLogs, isLoading: auditLogsLoading, error: auditLogsError } = useQuery<any[]>({
    queryKey: ['admin-logs'],
    queryFn: async () => {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 7); // Last 7 days
      const endDate = new Date();
      const response = await axios.get(`/admin/logs?startDate=${formatLocalDate(startDate)}&endDate=${formatLocalDate(endDate)}`);
      return response.data;
    },
    retry: 1,
    refetchOnWindowFocus: false
  });

  // Filter audit logs
  const filteredAuditLogs = auditLogs?.filter(log => {
    if (auditLogFilter === 'all') return true;
    return log.action === auditLogFilter;
  }) || [];

  // Fetch paginated wallet list
  const { data: walletListResponse, isLoading: walletListLoading, error: walletListError } = useQuery<WalletListResponse>({
    queryKey: ['wallet-list', walletListPage, walletListPageSize, walletSearchEmail],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', walletListPage.toString());
      params.append('pageSize', walletListPageSize.toString());
      if (walletSearchEmail.trim()) {
        params.append('searchEmail', walletSearchEmail.trim());
      }
      const response = await axios.get(`wallet/list?${params.toString()}`);
      return response.data;
    },
    retry: 1,
    refetchOnWindowFocus: false
  });

  const walletList = walletListResponse?.wallets || [];

  // Mutations
  const creditMutation = useMutation({
    mutationFn: async ({ userId, amount, type, reason }: { userId: number; amount: number; type: 'deposit' | 'withdraw'; reason?: string }) => {
      const response = await axios.post(`/admin/users/${userId}/credits`, {
        amount,
        type,
        reason
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-credit-transactions', selectedUser?.id] });
      setCreditDialog(false);
      setCreditAmount('');
      setCreditReason('');
      setSelectedUser(null);
      setShowTransactionHistory(false);
    },
  });

  const withdrawalMutation = useMutation({
    mutationFn: async ({ withdrawalId, action }: { withdrawalId: number; action: 'approve' | 'reject' }) => {
      const response = await axios.post(`/admin/withdrawals/${withdrawalId}/${action}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-withdrawals'] });
      queryClient.invalidateQueries({ queryKey: ['admin-stats'] });
    }
  });

  // Wallet mutations
  const searchWalletMutation = useMutation({
    mutationFn: async (email: string) => {
      const response = await axios.get(`wallet/user/email/${email}`);
      return response.data;
    },
    onSuccess: (data) => {
      setSearchedWallet(data);
    },
    onError: () => {
      setSearchedWallet(null);
      alert('Wallet not found for this user');
    }
  });



  const topupWalletMutation = useMutation({
    mutationFn: async (request: TopupRequest) => {
      const response = await axios.post('wallet/topup', request);
      return response.data;
    },
    onSuccess: () => {
      setTopupDialog(false);
      setTopupAmount('');
      setTopupNote('');
      queryClient.invalidateQueries({ queryKey: ['wallet-list'] });
      if (selectedWalletFromList) {
        setSelectedWalletFromList(prev => prev ? { ...prev, balance: prev.balance + parseFloat(topupAmount) } : null);
      }
      alert('Wallet topped up successfully');
    }
  });

  const adjustWalletMutation = useMutation({
    mutationFn: async ({ walletId, amount, note }: { walletId: number; amount: number; note?: string }) => {
      const response = await axios.post('wallet/adjust', {
        walletId,
        amount,
        note
      });
      return response.data;
    },
    onSuccess: () => {
      setManualAdjustDialog(false);
      setAdjustAmount('');
      setAdjustNote('');
      queryClient.invalidateQueries({ queryKey: ['wallet-list'] });
      alert('Wallet adjusted successfully');
    }
  });

  const walletTransactionsMutation = useMutation({
    mutationFn: async (walletId: number) => {
      const response = await axios.get(`wallet/${walletId}/transactions`);
      return response.data;
    },
    onSuccess: (data) => {
      setWalletTransactions(data);
    }
  });

  const createMissingWalletsMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post('admin/create-missing-wallets');
      return response.data;
    },
    onSuccess: (data) => {
      alert(`Successfully created ${data.walletsCreated} wallets for users without wallets`);
    },
    onError: (error: any) => {
       alert('Failed to create wallets: ' + (error.response?.data?.message || 'Unknown error'));
     }
   });



  const handleCreditSubmit = () => {
    if (!selectedUser || !creditAmount) return;
    
    const amount = parseFloat(creditAmount);
    
    // Validation
    if (amount <= 0) {
      alert('Amount must be greater than 0');
      return;
    }
    
    if (creditType === 'withdraw' && amount > (selectedUser.credits || 0)) {
      alert('Withdrawal amount cannot exceed current credits');
      return;
    }
    
    if (!creditReason.trim()) {
      alert('Please provide a reason for this transaction');
      return;
    }
    
    creditMutation.mutate({
      userId: selectedUser.id,
      amount,
      type: creditType,
      reason: creditReason.trim()
    });
  };

  const handleWithdrawalAction = (withdrawalId: number, action: 'approve' | 'reject') => {
    withdrawalMutation.mutate({ withdrawalId, action });
  };

  const handleGenerateReport = () => {
    if (reportDate) {
      // Implementation for generating PDF report
      console.log('Generating report for:', format(reportDate, 'yyyy-MM-dd'));
    }
  };

  // Wallet handlers
  const handleWalletSearch = () => {
    if (!walletSearchEmail.trim()) {
      alert('Please enter a user email');
      return;
    }
    searchWalletMutation.mutate(walletSearchEmail.trim());
  };



  const handleTopupWallet = () => {
    if (!searchedWallet) {
      alert('Please search for a wallet first');
      return;
    }
    
    const amount = parseFloat(topupAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid amount greater than 0');
      return;
    }

    topupWalletMutation.mutate({
      walletId: searchedWallet.id,
      amount,
      note: topupNote.trim() || undefined
    });
  };

  const handleManualAdjust = () => {
    if (!searchedWallet) {
      alert('Please search for a wallet first');
      return;
    }
    
    const amount = parseFloat(adjustAmount);
    if (isNaN(amount) || amount === 0) {
      alert('Please enter a valid amount (positive for credit, negative for debit)');
      return;
    }

    adjustWalletMutation.mutate({
      walletId: searchedWallet.id,
      amount,
      note: adjustNote.trim() || undefined
    });
  };

  const handleViewTransactions = () => {
    if (!searchedWallet) {
      alert('Please search for a wallet first');
      return;
    }
    walletTransactionsMutation.mutate(searchedWallet.id);
  };

  const handleCreateMissingWallets = () => {
    if (window.confirm('This will create wallets for all users who don\'t have wallets. Continue?')) {
      createMissingWalletsMutation.mutate();
    }
  };

  // Wallet list handlers
  const handleSelectWalletFromList = (wallet: Wallet) => {
    // Navigate to user transaction history page
    navigate(`/admin/user-transactions?userId=${wallet.userId}&userEmail=${encodeURIComponent(wallet.userEmail)}&userName=${encodeURIComponent(wallet.userName)}`);
  };

  const handleTopupFromList = (wallet: Wallet) => {
    setSelectedWalletFromList(wallet);
    setSearchedWallet(wallet);
    setTopupDialog(true);
  };

  const handleAdjustFromList = (wallet: Wallet) => {
    setSelectedWalletFromList(wallet);
    setSearchedWallet(wallet);
    setManualAdjustDialog(true);
  };

  const handleViewTransactionsFromList = (wallet: Wallet) => {
    setSelectedWalletFromList(wallet);
    setSearchedWallet(wallet);
    walletTransactionsMutation.mutate(wallet.id);
  };

  const handleWalletListPageChange = (newPage: number) => {
    setWalletListPage(newPage);
  };

  const handleWalletSearchChange = (email: string) => {
    setWalletSearchEmail(email);
    setWalletListPage(1); // Reset to first page when searching
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'pending': return 'warning';
      case 'suspended': return 'error';
      case 'approved': return 'info';
      case 'completed': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const formatCurrency = (amount: number | undefined | null) => {
    if (amount === undefined || amount === null || isNaN(amount)) {
      return '$0.00';
    }
    return `$${amount.toFixed(2)}`;
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return format(date, 'MMM dd, yyyy HH:mm');
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatShortDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return format(date, 'MMM dd, HH:mm');
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Filter users based on search term and status
  const filteredUsers = users?.filter((user: User) => {
    const matchesSearch = userSearchTerm === '' || 
      user.fullName.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(userSearchTerm.toLowerCase());
    
    let matchesStatus = true;
    if (userStatusFilter !== 'all') {
      if (userStatusFilter === 'active') {
        matchesStatus = user.isActive && !user.isSuspended;
      } else if (userStatusFilter === 'suspended') {
        matchesStatus = user.isSuspended;
      } else if (userStatusFilter === 'pending') {
        matchesStatus = !user.isActive && !user.isSuspended;
      }
    }
    
    return matchesSearch && matchesStatus;
  }) || [];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl">
        <Typography variant="h4" gutterBottom>
          Back Office Dashboard
        </Typography>

        <Paper sx={{ width: '100%' }}>
          <Tabs
            value={tabValue}
            onChange={(_, newValue) => setTabValue(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab icon={<DashboardIcon />} label="Dashboard" />
            <Tab icon={<People />} label="User Management" sx={{ display: 'none' }} />
            <Tab icon={<AccountBalance />} label="Withdrawals" sx={{ display: 'none' }} />
            <Tab icon={<AccountBalanceWallet />} label="Wallet Management" />
            <Tab icon={<Settings />} label="Bet Limits" sx={{ display: 'none' }} />
            <Tab icon={<Receipt />} label="Reports" sx={{ display: 'none' }} />
            <Tab icon={<MonetizationOn />} label="Payout Rules" />
            <Tab icon={<CalendarToday />} label="Draw Management" />
            <Tab icon={<EmojiEvents />} label="Results Entry" />
            <Tab icon={<Assessment />} label="Settlement & Reports" />
            <Tab icon={<BusinessCenter />} label="Operator Management" />
            <Tab icon={<Percent />} label="Commission Management" />
          </Tabs>

          {/* Dashboard Tab */}
          <TabPanel value={tabValue} index={0}>
            {statsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Failed to load dashboard statistics. Please try refreshing the page.
              </Alert>
            )}
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography color="text.secondary" gutterBottom>
                          Total Users
                        </Typography>
                        <Typography variant="h4">
                          {adminStats?.totalUsers.toLocaleString()}
                        </Typography>
                      </Box>
                      <People sx={{ fontSize: 40, color: 'primary.main' }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography color="text.secondary" gutterBottom>
                          Daily Revenue
                        </Typography>
                        <Typography variant="h4" color="success.main">
                          {formatCurrency(adminStats?.dailyRevenue || 0)}
                        </Typography>
                      </Box>
                      <TrendingUp sx={{ fontSize: 40, color: 'success.main' }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography color="text.secondary" gutterBottom>
                          Monthly Revenue
                        </Typography>
                        <Typography variant="h4" color="info.main">
                          {formatCurrency(adminStats?.monthlyRevenue || 0)}
                        </Typography>
                      </Box>
                      <AttachMoney sx={{ fontSize: 40, color: 'info.main' }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography color="text.secondary" gutterBottom>
                          Pending Withdrawals
                        </Typography>
                        <Typography variant="h4" color="warning.main">
                          {adminStats?.pendingWithdrawals}
                        </Typography>
                      </Box>
                      <AccountBalance sx={{ fontSize: 40, color: 'warning.main' }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Financial Overview
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Total Bets:</Typography>
                        <Typography fontWeight="bold">
                          {formatCurrency(adminStats?.totalBets || 0)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Total Winnings:</Typography>
                        <Typography fontWeight="bold" color="error.main">
                          {formatCurrency(adminStats?.totalWinnings || 0)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Total Deposits:</Typography>
                        <Typography fontWeight="bold" color="success.main">
                          {formatCurrency(adminStats?.totalDeposits || 0)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Total Withdrawals:</Typography>
                        <Typography fontWeight="bold" color="warning.main">
                          {formatCurrency(adminStats?.totalWithdrawals || 0)}
                        </Typography>
                      </Box>
                      <Divider />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="h6">Net Profit:</Typography>
                        <Typography variant="h6" color="success.main">
                          {formatCurrency(
                            (adminStats?.totalBets || 0) - 
                            (adminStats?.totalWinnings || 0)
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">
                        User Reports Summary
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => setTabValue(6)}
                        startIcon={<MonetizationOn />}
                      >
                        View Payout Rules
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Active Users:</Typography>
                        <Typography fontWeight="bold" color="success.main">
                          {filteredUsers.filter((u: User) => u.isActive && !u.isSuspended).length}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Suspended Users:</Typography>
                        <Typography fontWeight="bold" color="error.main">
                          {filteredUsers.filter((u: User) => u.isSuspended).length}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Inactive Users:</Typography>
                        <Typography fontWeight="bold" color="warning.main">
                          {filteredUsers.filter((u: User) => !u.isActive && !u.isSuspended).length}
                        </Typography>
                      </Box>
                      <Divider />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Total Credits:</Typography>
                        <Typography fontWeight="bold">
                          {formatCurrency(filteredUsers.reduce((sum: number, user: User) => sum + (user.credits || 0), 0))}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Avg Credits/User:</Typography>
                        <Typography fontWeight="bold">
                          {formatCurrency(filteredUsers.length > 0 ? filteredUsers.reduce((sum: number, user: User) => sum + (user.credits || 0), 0) / filteredUsers.length : 0)}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Quick Actions
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Button
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={handleGenerateReport}
                        fullWidth
                      >
                        Generate Daily Report
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<AccountBalanceWallet />}
                        onClick={() => setTabValue(3)}
                        fullWidth
                      >
                        Manage Wallets
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<MonetizationOn />}
                        onClick={() => setTabValue(6)}
                        fullWidth
                      >
                        Payout Rules
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<CalendarToday />}
                        onClick={() => setTabValue(7)}
                        fullWidth
                      >
                        Draw Management
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<EmojiEvents />}
                        onClick={() => setTabValue(8)}
                        fullWidth
                      >
                        Results Entry
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<TrendingUp />}
                        onClick={() => navigate('/admin/user-sales-report')}
                        fullWidth
                      >
                        User Sales Report
                      </Button>
                      <Button
                         variant="contained"
                         color="primary"
                         startIcon={<AttachMoney />}
                         onClick={() => {
                           setUserSelectionDialog(true);
                           setQuickTopUpSearchTerm('');
                         }}
                         fullWidth
                       >
                         Quick Credit Top-Up
                       </Button>
                     </Box>
                   </CardContent>
                 </Card>
               </Grid>

               {/* Audit Logs Widget */}
               <Grid size={{ xs: 12 }}>
                 <Card>
                   <CardContent>
                     <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                       <Typography variant="h6">
                         Recent Admin Activities
                       </Typography>
                       <Box sx={{ display: 'flex', gap: 1 }}>
                         <FormControl size="small" sx={{ minWidth: 120 }}>
                           <InputLabel>Filter</InputLabel>
                           <Select
                             value={auditLogFilter}
                             label="Filter"
                             onChange={(e) => setAuditLogFilter(e.target.value)}
                           >
                             <MenuItem value="all">All Actions</MenuItem>
                             <MenuItem value="credit_deposit">Credit Deposits</MenuItem>
                             <MenuItem value="credit_withdraw">Credit Withdrawals</MenuItem>
                             <MenuItem value="user_status_change">Status Changes</MenuItem>
                             <MenuItem value="withdrawal_approval">Withdrawal Approvals</MenuItem>
                             <MenuItem value="withdrawal_rejection">Withdrawal Rejections</MenuItem>
                           </Select>
                         </FormControl>
                         <Button
                           size="small"
                           onClick={() => setShowAuditLogs(!showAuditLogs)}
                           variant={showAuditLogs ? 'contained' : 'outlined'}
                         >
                           {showAuditLogs ? 'Hide Details' : 'Show Details'}
                         </Button>
                       </Box>
                     </Box>
                     
                     <Box sx={{ maxHeight: showAuditLogs ? 400 : 200, overflow: 'auto' }}>
                       {filteredAuditLogs.slice(0, showAuditLogs ? undefined : 5).map((log) => {
                         const getActionColor = (action: string) => {
                           switch (action) {
                             case 'credit_deposit': return 'success';
                             case 'credit_withdraw': return 'warning';
                             case 'user_status_change': return 'info';
                             case 'withdrawal_approval': return 'success';
                             case 'withdrawal_rejection': return 'error';
                             default: return 'default';
                           }
                         };
                         
                         const getActionLabel = (action: string) => {
                           switch (action) {
                             case 'credit_deposit': return 'Credit Deposit';
                             case 'credit_withdraw': return 'Credit Withdrawal';
                             case 'user_status_change': return 'Status Change';
                             case 'withdrawal_approval': return 'Withdrawal Approved';
                             case 'withdrawal_rejection': return 'Withdrawal Rejected';
                             case 'user_creation': return 'User Created';
                             case 'user_deletion': return 'User Deleted';
                             default: return action;
                           }
                         };
                         
                         return (
                           <Box key={log.id} sx={{ 
                             display: 'flex', 
                             justifyContent: 'space-between', 
                             alignItems: 'center',
                             p: 1,
                             mb: 1,
                             bgcolor: 'grey.50',
                             borderRadius: 1,
                             border: '1px solid',
                             borderColor: 'grey.200'
                           }}>
                             <Box sx={{ flex: 1 }}>
                               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                 <Chip
                                   size="small"
                                   label={getActionLabel(log.action)}
                                   color={getActionColor(log.action) as any}
                                 />
                                 <Typography variant="body2" fontWeight="bold">
                                   {log.adminName}
                                 </Typography>
                               </Box>
                               <Typography variant="body2" color="text.secondary">
                                 {log.details}
                               </Typography>
                               {log.targetUserEmail && (
                                 <Typography variant="caption" color="text.secondary">
                                   Target: {log.targetUserEmail}
                                 </Typography>
                               )}
                               {showAuditLogs && log.previousValue && log.newValue && (
                                 <Typography variant="caption" sx={{ display: 'block' }}>
                                   Changed from: {log.previousValue} → {log.newValue}
                                 </Typography>
                               )}
                             </Box>
                             <Box sx={{ textAlign: 'right' }}>
                               <Typography variant="caption" color="text.secondary">
                                 {formatShortDate(log.timestamp)}
                               </Typography>
                               {showAuditLogs && log.ipAddress && (
                                 <Typography variant="caption" sx={{ display: 'block' }} color="text.secondary">
                                   IP: {log.ipAddress}
                                 </Typography>
                               )}
                             </Box>
                           </Box>
                         );
                       })}
                       
                       {filteredAuditLogs.length === 0 && (
                         <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 2 }}>
                           No audit logs found
                         </Typography>
                       )}
                       
                       {!showAuditLogs && filteredAuditLogs.length > 5 && (
                         <Typography variant="caption" color="text.secondary" textAlign="center" sx={{ display: 'block', mt: 1 }}>
                           Showing 5 of {filteredAuditLogs.length} activities
                         </Typography>
                       )}
                     </Box>
                   </CardContent>
                 </Card>
               </Grid>
             </Grid>
           </TabPanel>

          {/* User Management Tab */}
          <TabPanel value={tabValue} index={1}>
            <UserManagement />
          </TabPanel>

          {/* Withdrawals Tab */}
          <TabPanel value={tabValue} index={2}>
            <Typography variant="h6" gutterBottom>
              Withdrawal Requests
            </Typography>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Bank Details</TableCell>
                    <TableCell>Request Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {withdrawals?.map((request) => (
                    <TableRow key={request.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {request.userEmail}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(request.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {request.bankName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {request.accountNumber}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {request.accountHolderName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(request.requestDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={request.status}
                          color={getStatusColor(request.status) as any}
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {request.status === 'pending' && (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              onClick={() => handleWithdrawalAction(request.id, 'approve')}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              onClick={() => handleWithdrawalAction(request.id, 'reject')}
                            >
                              Reject
                            </Button>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Bet Limits Tab */}
          <TabPanel value={tabValue} index={4}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6">Bet Limits Management</Typography>
              <Button variant="contained" startIcon={<Add />}>
                Add Limit
              </Button>
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Counter</TableCell>
                    <TableCell>Number</TableCell>
                    <TableCell align="right">Max Amount</TableCell>
                    <TableCell align="right">Current Amount</TableCell>
                    <TableCell align="right">Remaining</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {betLimits?.map((limit) => (
                    <TableRow key={limit.id} hover>
                      <TableCell>{limit.counter}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
                          {limit.number}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(limit.maxAmount)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(limit.currentAmount)}
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          color={limit.currentAmount >= limit.maxAmount * 0.9 ? 'error.main' : 'text.primary'}
                          fontWeight="bold"
                        >
                          {formatCurrency(limit.maxAmount - limit.currentAmount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small">
                          <Edit />
                        </IconButton>
                        <IconButton size="small">
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Reports Tab */}
          <TabPanel value={tabValue} index={5}>
            <UserReports />
          </TabPanel>

          {/* Payout Rules Tab */}
          <TabPanel value={tabValue} index={6}>
            <PayoutRulesManagement />
          </TabPanel>

          {/* Wallet Management Tab */}
          <TabPanel value={tabValue} index={3}>
            <Typography variant="h6" gutterBottom>
              Wallet Management
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Wallet Search & Filter
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  label="Search by User Email"
                  variant="outlined"
                  size="small"
                  value={walletSearchEmail}
                  onChange={(e) => handleWalletSearchChange(e.target.value)}
                  sx={{ minWidth: 300 }}
                  placeholder="Enter email to filter wallets..."
                />
                <Button 
                  variant="outlined" 
                  startIcon={<FilterList />}
                  onClick={() => setWalletSearchEmail('')}
                  disabled={!walletSearchEmail}
                >
                  Clear Filter
                </Button>
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button 
                  variant="contained" 
                  startIcon={<Add />} 
                  color="success"
                  onClick={handleCreateMissingWallets}
                >
                  Create Missing Wallets
                </Button>
              </Box>
            </Box>

            {/* Wallet List Summary */}
            {walletListResponse && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {walletList.length} of {walletListResponse.totalCount} wallets
                  {walletSearchEmail && ` (filtered by "${walletSearchEmail}")`}
                </Typography>
              </Box>
            )}

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User ID</TableCell>
                    <TableCell>User Email</TableCell>
                    <TableCell>User Name</TableCell>
                    <TableCell align="right">Balance</TableCell>
                    <TableCell>Currency</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created Date</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {walletListLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="text.secondary">
                          Loading wallets...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : walletListError ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="error">
                          Error loading wallets. Please try again.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (walletListResponse?.wallets || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="text.secondary">
                          {walletSearchEmail ? 'No wallets found matching your search.' : 'No wallets found.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    (walletListResponse?.wallets || []).map((wallet) => (
                      <TableRow 
                        key={wallet.id}
                        sx={{ 
                          backgroundColor: selectedWalletFromList?.id === wallet.id ? 'action.selected' : 'inherit',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                        onClick={() => handleSelectWalletFromList(wallet)}
                      >
                        <TableCell>{wallet.userId}</TableCell>
                        <TableCell>{wallet.userEmail}</TableCell>
                        <TableCell>{wallet.userName || 'N/A'}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold" color="primary.main">
                            {formatCurrency(wallet.balance)}
                          </Typography>
                        </TableCell>
                        <TableCell>{wallet.currency}</TableCell>
                        <TableCell>
                          <Chip
                            label={wallet.status}
                            color={getStatusColor(wallet.status) as any}
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell>{formatDate(wallet.createdAt)}</TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTopupFromList(wallet);
                              }}
                              title="Top Up"
                            >
                              <AttachMoney />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              color="warning"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAdjustFromList(wallet);
                              }}
                              title="Manual Adjust"
                            >
                              <Edit />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              color="info"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewTransactionsFromList(wallet);
                              }}
                              title="View Transactions"
                            >
                              <Receipt />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {walletListResponse && walletListResponse.totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={walletListPage === 1}
                    onClick={() => handleWalletListPageChange(walletListPage - 1)}
                  >
                    Previous
                  </Button>
                  <Typography variant="body2" sx={{ mx: 2 }}>
                    Page {walletListPage} of {walletListResponse.totalPages}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={walletListPage === walletListResponse.totalPages}
                    onClick={() => handleWalletListPageChange(walletListPage + 1)}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            )}

            {/* Wallet Transactions Table */}
            {walletTransactions.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Recent Transactions
                </Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell align="right">Before</TableCell>
                        <TableCell align="right">After</TableCell>
                        <TableCell>Note</TableCell>
                        <TableCell>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {walletTransactions.map((transaction) => (
                        <TableRow key={transaction.id}>
                          <TableCell>
                            <Chip
                              label={transaction.txType}
                              size="small"
                              color={transaction.amount > 0 ? 'success' : 'error'}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography 
                              variant="body2" 
                              color={transaction.amount > 0 ? 'success.main' : 'error.main'}
                              fontWeight="bold"
                            >
                              {transaction.amount > 0 ? '+' : ''}{formatCurrency(transaction.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{formatCurrency(transaction.BeforeBalance)}</TableCell>
                          <TableCell align="right">{formatCurrency(transaction.AfterBalance)}</TableCell>
                          <TableCell>{transaction.note || '-'}</TableCell>
                          <TableCell>{formatDate(transaction.createdAt)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </TabPanel>

          {/* Draw Management Tab */}
          <TabPanel value={tabValue} index={7}>
            <DrawManagement />
          </TabPanel>

          {/* Results Entry Tab */}
          <TabPanel value={tabValue} index={8}>
            <ResultsEntry />
          </TabPanel>

          {/* Settlement & Reports Tab */}
          <TabPanel value={tabValue} index={9}>
            <Box sx={{ mb: 3 }}>
              <Button
                variant="contained"
                startIcon={<TrendingUp />}
                onClick={() => navigate('/admin/user-sales-report')}
                sx={{ mb: 2 }}
              >
                View User Sales Commission Report
              </Button>
            </Box>
            <SettlementDashboard />
          </TabPanel>

          {/* Operator Management Tab */}
          <TabPanel value={tabValue} index={10}>
            <OperatorManagement />
          </TabPanel>

          {/* Commission Management Tab */}
          <TabPanel value={tabValue} index={11}>
            <CommissionManagement />
          </TabPanel>
        </Paper>

        {/* Credit Management Dialog */}
        <Dialog open={creditDialog} onClose={() => setCreditDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Manage Credits - {selectedUser?.fullName}</Typography>
              <Button
                size="small"
                onClick={() => setShowTransactionHistory(!showTransactionHistory)}
                startIcon={<Receipt />}
              >
                {showTransactionHistory ? 'Hide' : 'Show'} History
              </Button>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Alert severity="info">
                Current Credits: {formatCurrency(selectedUser?.credits || 0)}
              </Alert>
              
              <Grid container spacing={2}>
                <Grid {...({item: true, xs: 12, md: showTransactionHistory ? 6 : 12} as GridProps)}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControl fullWidth>
                      <InputLabel>Action</InputLabel>
                      <Select
                        value={creditType}
                        label="Action"
                        onChange={(e) => setCreditType(e.target.value as 'deposit' | 'withdraw')}
                      >
                        <MenuItem value="deposit">Deposit Credits</MenuItem>
                        <MenuItem value="withdraw">Withdraw Credits</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <TextField
                      label="Amount"
                      type="number"
                      value={creditAmount}
                      onChange={(e) => setCreditAmount(e.target.value)}
                      inputProps={{ min: 0, step: 0.01 }}
                      fullWidth
                      helperText={creditType === 'withdraw' ? `Max: ${formatCurrency(selectedUser?.credits || 0)}` : ''}
                    />
                    
                    <TextField
                      label="Reason (Required)"
                      value={creditReason}
                      onChange={(e) => setCreditReason(e.target.value)}
                      multiline
                      rows={2}
                      fullWidth
                      placeholder="Enter reason for this transaction..."
                    />
                  </Box>
                </Grid>
                
                {showTransactionHistory && (
                  <Grid {...({item: true, xs: 12, md: 6} as GridProps)}>
                    <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Recent Transactions
                      </Typography>
                      {creditTransactions && creditTransactions.length > 0 ? (
                        creditTransactions.map((transaction) => (
                          <Card key={transaction.id} sx={{ mb: 1, p: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box>
                                <Typography variant="body2" fontWeight="bold">
                                  {transaction.type === 'deposit' ? '+' : '-'}{formatCurrency(transaction.amount)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {format(new Date(transaction.timestamp), 'MMM dd, yyyy HH:mm')}
                                </Typography>
                              </Box>
                              <Chip
                                size="small"
                                label={transaction.type}
                                color={transaction.type === 'deposit' ? 'success' : 'warning'}
                              />
                            </Box>
                            {transaction.reason && (
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                {transaction.reason}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              By: {transaction.adminName}
                            </Typography>
                          </Card>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No transaction history available
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setCreditDialog(false);
              setShowTransactionHistory(false);
              setCreditAmount('');
              setCreditReason('');
            }}>Cancel</Button>
            <Button 
              onClick={handleCreditSubmit}
              variant="contained"
              disabled={!creditAmount || !creditReason.trim() || creditMutation.isPending}
            >
              {creditMutation.isPending ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* User Selection Dialog for Quick Credit Top-Up */}
        <Dialog open={userSelectionDialog} onClose={() => setUserSelectionDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            Select User for Credit Top-Up
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Search Users"
                value={quickTopUpSearchTerm}
                onChange={(e) => setQuickTopUpSearchTerm(e.target.value)}
                placeholder="Search by name, email, or referral code..."
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Box>
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Current Credits</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users?.filter((user: User) => {
                    const searchLower = quickTopUpSearchTerm.toLowerCase();
                    return searchLower === '' || 
                      user.fullName.toLowerCase().includes(searchLower) ||
                      user.email.toLowerCase().includes(searchLower);
                  }).map((user: User) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.fullName}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{formatCurrency(user.credits)}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.isSuspended ? 'Suspended' : (user.isActive ? 'Active' : 'Inactive')}
                          color={user.isSuspended ? 'error' : (user.isActive ? 'success' : 'default')}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AttachMoney />}
                          onClick={() => {
                            setSelectedUser(user);
                            setUserSelectionDialog(false);
                            setCreditDialog(true);
                            setCreditType('deposit');
                            setCreditAmount('');
                            setCreditReason('');
                          }}
                          disabled={user.isSuspended || !user.isActive}
                        >
                          Top Up
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUserSelectionDialog(false)}>Cancel</Button>
            <Button 
              onClick={() => {
                setUserSelectionDialog(false);
                setTabValue(1); // Navigate to User Management tab
              }}
              variant="outlined"
            >
              Go to User Management
            </Button>
          </DialogActions>
        </Dialog>



        {/* Top Up Wallet Dialog */}
        <Dialog open={topupDialog} onClose={() => setTopupDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Top Up Wallet</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {searchedWallet && (
                <Alert severity="info">
                  Current Balance: {formatCurrency(searchedWallet.balance)} {searchedWallet.currency}
                </Alert>
              )}
              <TextField
                label="Top Up Amount"
                type="number"
                value={topupAmount}
                onChange={(e) => setTopupAmount(e.target.value)}
                inputProps={{ min: 0, step: 0.01 }}
                fullWidth
              />
              <TextField
                label="Note (Optional)"
                value={topupNote}
                onChange={(e) => setTopupNote(e.target.value)}
                multiline
                rows={2}
                fullWidth
                placeholder="Enter note for this top up..."
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTopupDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleTopupWallet}
              variant="contained"
              disabled={topupWalletMutation.isPending}
            >
              {topupWalletMutation.isPending ? 'Processing...' : 'Top Up'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Manual Adjust Wallet Dialog */}
        <Dialog open={manualAdjustDialog} onClose={() => setManualAdjustDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Manual Wallet Adjustment</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {searchedWallet && (
                <Alert severity="info">
                  Current Balance: {formatCurrency(searchedWallet.balance)} {searchedWallet.currency}
                </Alert>
              )}
              <Alert severity="warning">
                Enter positive amount for credit, negative amount for debit
              </Alert>
              <TextField
                label="Adjustment Amount"
                type="number"
                value={adjustAmount}
                onChange={(e) => setAdjustAmount(e.target.value)}
                inputProps={{ step: 0.01 }}
                fullWidth
                helperText="Positive for credit (+), negative for debit (-)"
              />
              <TextField
                label="Note (Optional)"
                value={adjustNote}
                onChange={(e) => setAdjustNote(e.target.value)}
                multiline
                rows={2}
                fullWidth
                placeholder="Enter reason for this adjustment..."
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setManualAdjustDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleManualAdjust}
              variant="contained"
              disabled={adjustWalletMutation.isPending}
            >
              {adjustWalletMutation.isPending ? 'Processing...' : 'Adjust'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
};

export default BackOffice;